// ==========================================================================
// components/AccountMenu.tsx — the little menu top right (FF-058, 10 Oct 2026)
//   James: "Add a update app function in the settings / control panel (maybe a
//   little drop down top right) makes updating easier when it's saved as an app
//   on home screen."
//   Who's signed in · My profile · colours · light/dark · Update app · Sign out.
// ==========================================================================
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Loader2, LogOut, RefreshCw, UserRound } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/lib/auth";
import { useMe, useMembership } from "@/lib/household";
import { tokenColour } from "@/lib/calendar";
import { MODES, useMode } from "@/lib/appearance";
import { BUILT_AT, checkForUpdate, type UpdateResult } from "@/lib/pwa";
import ThemeDots from "./ThemeDots";

export function Avatar({ size = "h-9 w-9 text-sm" }: { size?: string }) {
  const me = useMe();
  return <span className={`flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${size}`} style={{ background: tokenColour(me?.colour ?? "chart-1") }}>{me?.display_name?.[0] ?? "·"}</span>;
}

/** "Update app" with its own little status line. Used here and in My profile. */
export function UpdateApp({ className = "" }: { className?: string }) {
  const [state, setState] = useState<"idle" | "checking" | UpdateResult>("idle");
  const run = async () => { setState("checking"); try { setState(await checkForUpdate()); } catch { setState("latest"); } };
  const note = state === "latest" ? "You're on the latest version." : state === "updating" ? "New version found. Reloading…" : state === "not-installed" ? "This copy isn't the installed app (it's local or a private window). Press F5 or reload instead." : `Version from ${format(BUILT_AT, "d MMM, h:mmaaa")}`;
  return (
    <div className={className}>
      <button type="button" onClick={run} disabled={state === "checking" || state === "updating"}
        className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[15px] hover:bg-surface-raised disabled:opacity-60">
        {state === "checking" || state === "updating" ? <Loader2 className="h-[18px] w-[18px] animate-spin text-primary" /> : <RefreshCw className="h-[18px] w-[18px] text-primary" />}
        <span className="min-w-0 flex-1"><span className="block font-medium">Update app</span><span className={`block text-xs ${state === "latest" ? "text-good" : "text-ink-muted"}`}>{note}</span></span>
      </button>
    </div>
  );
}

export default function AccountMenu({ onPanel = false }: { onPanel?: boolean }) {
  const { session, signOut } = useAuth(); const me = useMe();
  const membership = useMembership();
  const [open, setOpen] = useState(false); const [mode, setMode] = useMode();
  const box = useRef<HTMLDivElement>(null); const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", away); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  return (
    <div ref={box} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label="Account and settings"
        className={`flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 transition ${onPanel ? "text-panel-ink hover:bg-panel-raised" : "bg-surface text-ink shadow-soft ring-1 ring-line/70 hover:ring-primary/40"}`}>
        <Avatar size="h-8 w-8 text-sm" />
        <span className={`hidden text-sm font-medium sm:inline ${onPanel ? "" : ""}`}>{me?.display_name ?? "Me"}</span>
        <ChevronDown className={`h-4 w-4 transition ${open ? "rotate-180" : ""} ${onPanel ? "text-panel-muted" : "text-ink-muted"}`} />
      </button>

      {open && (
        <div role="menu" className="absolute right-0 top-full z-50 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-lg bg-surface p-2 text-ink shadow-soft ring-1 ring-line">
          <div className="flex items-center gap-3 px-3 pb-3 pt-2">
            <Avatar size="h-11 w-11 text-base" />
            <div className="min-w-0"><div className="truncate font-semibold">{me?.full_name || me?.display_name || "Signed in"}</div><div className="truncate text-xs text-ink-muted">{session?.user.email}</div></div>
          </div>
          <div className="border-t border-line pt-1.5">
            <Link to="/profile" role="menuitem" className="flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-medium hover:bg-surface-raised"><UserRound className="h-[18px] w-[18px] text-primary" /> My profile</Link>
            {!membership.isError && membership.data?.role === 'admin' && <Link to="/admin" role="menuitem" className="flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-medium hover:bg-surface-raised">Admin Centre</Link>}
          </div>
          <div className="space-y-3 px-3 py-3">
            <div className="flex items-center justify-between gap-3"><span className="text-sm text-ink-muted">Colours</span><ThemeDots /></div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-ink-muted">Light or dark</span>
              <div className="flex rounded-full bg-surface-raised p-0.5">
                {MODES.map((m) => (
                  <button key={m.key} type="button" onClick={() => setMode(m.key)} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${mode === m.key ? "bg-surface text-ink shadow-soft" : "text-ink-muted"}`}>{m.label}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-line pt-1.5"><UpdateApp /></div>
          <div className="border-t border-line pt-1.5">
            <button type="button" onClick={signOut} role="menuitem" className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-[15px] text-ink-muted hover:bg-surface-raised hover:text-ink"><LogOut className="h-[18px] w-[18px]" /> Sign out</button>
          </div>
        </div>
      )}
    </div>
  );
}
